import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { ArrowLeft, Mail, Phone, MapPin, Send, CheckCircle } from 'lucide-react';
import SEO from '../components/SEO';
import api from '../utils/api';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await api.post('/contact', formData);
      setSubmitted(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
        {/* Header */}
        <header className="bg-gradient-to-r from-slate-900 to-slate-800 text-white">
          <nav className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
            <Link to="/" className="flex items-center gap-2 text-xl font-bold">
              <span className="text-2xl">🚛</span> MoveMate
            </Link>
            <div className="flex gap-4">
              <Link to="/login" className="px-4 py-2 rounded-lg hover:bg-white/10 transition">
                Login
              </Link>
              <Link to="/register-company" className="px-4 py-2 bg-blue-600 rounded-lg hover:bg-blue-700 transition">
                Start Free Trial
              </Link>
            </div>
          </nav>
        </header>

        <div className="max-w-lg mx-auto px-6 py-20 text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="text-green-600" size={40} />
          </div>
          <h1 className="text-3xl font-bold mb-4 text-slate-900">Message Sent!</h1>
          <p className="text-lg text-slate-600 mb-8">
            Thank you for contacting us. We'll get back to you within 24 hours.
          </p>
          <Link to="/">
            <Button className="px-8">Back to Home</Button>
          </Link>
        </div>

        {/* Footer */}
        <footer className="bg-slate-900 text-slate-400 py-8 mt-auto">
          <div className="max-w-7xl mx-auto px-6 text-center">
            <p>&copy; 2025 MoveMate. All rights reserved.</p>
            <p className="mt-2">Powered by Aztrix Media Ltd.</p>
          </div>
        </footer>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <SEO 
        title="Contact Us - Get in Touch"
        description="Have questions about MoveMate? Contact our team for support, demos, or sales inquiries. We're here to help your removal company succeed."
        url="https://admin.movematebend.co.uk/contact"
      />
      {/* Header */}
      <header className="bg-gradient-to-r from-slate-900 to-slate-800 text-white">
        <nav className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <Link to="/" className="flex items-center gap-2 text-xl font-bold">
            <img src="/logo.png" alt="MoveMate" className="h-8" /> MoveMate
          </Link>
          <div className="flex gap-4">
            <Link to="/login" className="px-4 py-2 rounded-lg hover:bg-white/10 transition">
              Login
            </Link>
            <Link to="/register-company" className="px-4 py-2 bg-blue-600 rounded-lg hover:bg-blue-700 transition">
              Start Free Trial
            </Link>
          </div>
        </nav>
      </header>

      {/* Back Link */}
      <div className="max-w-6xl mx-auto px-6 pt-8">
        <Link to="/" className="inline-flex items-center text-slate-600 hover:text-slate-900 transition">
          <ArrowLeft size={18} className="mr-2" />
          Back to Home
        </Link>
      </div>

      {/* Main Content */}
      <section className="max-w-6xl mx-auto px-6 py-12">
        <div className="grid lg:grid-cols-2 gap-12">
          {/* Left Column - Info */}
          <div>
            <h1 className="text-4xl md:text-5xl font-bold mb-4 text-slate-900">
              Get in Touch
            </h1>
            <p className="text-xl text-slate-600 mb-8">
              Have questions about MoveMate? Want to schedule a demo? We'd love to hear from you.
            </p>

            <div className="space-y-6 mb-12">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Mail className="text-blue-600" size={22} />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900 mb-1">Email Us</h3>
                  <p className="text-slate-600">info@movematebend.co.uk</p>
                  <p className="text-slate-500 text-sm">We'll respond within 24 hours</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Phone className="text-green-600" size={22} />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900 mb-1">Call Us</h3>
                  <p className="text-slate-600">+44 (0) 123 456 7890</p>
                  <p className="text-slate-500 text-sm">Mon-Fri, 9am-5pm GMT</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <MapPin className="text-purple-600" size={22} />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900 mb-1">Office</h3>
                  <p className="text-slate-600">Aztrix Media Ltd.</p>
                  <p className="text-slate-500 text-sm">United Kingdom</p>
                </div>
              </div>
            </div>

            {/* Quick Links */}
            <div className="bg-slate-100 rounded-2xl p-6">
              <h3 className="font-semibold text-slate-900 mb-4">Quick Links</h3>
              <div className="space-y-2">
                <Link to="/faq" className="block text-blue-600 hover:underline">
                  → Frequently Asked Questions
                </Link>
                <Link to="/register-company" className="block text-blue-600 hover:underline">
                  → Start Your Free Trial
                </Link>
              </div>
            </div>
          </div>

          {/* Right Column - Form */}
          <div className="bg-white rounded-2xl shadow-xl border border-slate-200 p-8">
            <h2 className="text-2xl font-bold mb-6 text-slate-900">Send us a Message</h2>
            
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name">Full Name *</Label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="mt-1"
                    placeholder="John Smith"
                    data-testid="contact-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="mt-1"
                    placeholder="john@example.com"
                    data-testid="contact-email-input"
                  />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="company">Company Name</Label>
                  <Input
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    className="mt-1"
                    placeholder="ABC Removals Ltd"
                    data-testid="contact-company-input"
                  />
                </div>
                <div>
                  <Label htmlFor="phone">Phone Number</Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    className="mt-1"
                    placeholder="+44 123 456 7890"
                    data-testid="contact-phone-input"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="subject">Subject *</Label>
                <Input
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  required
                  className="mt-1"
                  placeholder="How can we help?"
                  data-testid="contact-subject-input"
                />
              </div>

              <div>
                <Label htmlFor="message">Message *</Label>
                <Textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  className="mt-1 min-h-[150px]"
                  placeholder="Tell us more about your inquiry..."
                  data-testid="contact-message-input"
                />
              </div>

              <Button
                type="submit"
                className="w-full h-12 text-base font-semibold"
                disabled={loading}
                data-testid="contact-submit-btn"
              >
                {loading ? (
                  'Sending...'
                ) : (
                  <>
                    <Send className="mr-2" size={18} />
                    Send Message
                  </>
                )}
              </Button>
            </form>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-slate-400 py-8 mt-16">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <p>&copy; 2025 MoveMate. All rights reserved.</p>
          <p className="mt-2">Powered by Aztrix Media Ltd.</p>
        </div>
      </footer>
    </div>
  );
};

export default ContactPage;
